<template>
  <chart :option="calOptions" ref="chart" class="chart-cont"></chart>
</template>

<script>
import lodash, { merge } from 'lodash';
import numeral from 'numeral';
import dayjs from 'dayjs';
import Chart from '@/components/echarts/chart.vue';
import { optionGen } from '@/utils/helper';
import { DEFAULT_TIME_UNIT_ENUM } from '@/views/analysis/retention-analysis/components/config';

export default {
  name: 'RetentionDataChart',
  components: {
    Chart,
  },
  props: {
    analysisData: {
      type: Array,
      default: () => [],
    },
    timeUnit: {
      type: String,
    },
    followStartDay: {
      type: Number,
    },
    followEndDay: {
      type: Number,
    },
    chartShowType: {
      type: Number,
    },
    chartShowDate: {
      type: String,
    },
    isCalcToday: {
      type: Number,
    },
  },
  data() {
    return {
      xAxisData: [],
      seriesData: [],
      legendData: [],
    };
  },
  computed: {
    calOptions() {
      const options = optionGen();

      merge(options, {
        legend: {
          show: true,
          data: this.legendData,
        },
        tooltip: {
          show: true,
          formatter: params => {
            const { name } = params[0];
            let str = `${name}`;
            params.forEach(({ marker, seriesName, data }) => {
              str += `</br>${marker} ${seriesName}： ${this.ratioFormat(data)}`;
            });
            return str;
          },
        },
        xAxis: { type: 'category', data: this.xAxisData },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: value => this.ratioFormat(value),
          },
        },
        grid: {
          top: 15,
          left: '1%',
          right: '1%',
          bottom: 30,
          containLabel: true,
        },
        series: this.seriesData,
      });

      return options;
    },
  },
  watch: {
    analysisData: {
      handler() {
        this.translateAnalysisData();
      },
      deep: true,
    },
    chartShowType: {
      handler() {
        this.translateAnalysisData();
      },
      deep: true,
    },
    chartShowDate: {
      handler() {
        this.translateAnalysisData();
      },
      deep: true,
    },
  },
  mounted() {
    this.translateAnalysisData();
  },
  methods: {
    translateAnalysisData() {
      const { analysisData } = this;
      if (!analysisData || !analysisData.length) {
        return;
      }
      this.legendData = this.getLegendData();
      this.xAxisData = this.getXAxisData();
      this.seriesData = this.getSeriesData();
    },
    getLegendData() {
      const legendData = [];
      this.analysisData.forEach(analysisDataItem => {
        analysisDataItem.dataList.forEach((item, index) => {
          const name = this.getLegendName(
            item.showLabel,
            index,
            item.globalFilerName,
            item.groupBy,
            item.eventChineseName,
            item.eventName,
          );
          legendData.push({ name });
        });
      });
      return legendData.slice(0, 10);
    },
    getXAxisData() {
      const xAxisData = [];
      const { dateList } = this.analysisData[0];
      const { timeUnit, chartShowType } = this;

      // 趋势
      if (chartShowType === 1) {
        const region = this.getRegion();
        for (let i = 0; i < region.length; i++) {
          xAxisData.push(this.getRegionItemText(region[i]));
        }
      }

      // 对比
      if (chartShowType === 2) {
        dateList.forEach(item => {
          xAxisData.push(this.getEventNameText(item));
        });
      }

      return xAxisData.slice(0, 10);
    },
    getSeriesData() {
      const { chartShowType, chartShowDate } = this;
      const seriesData = [];
      this.analysisData.forEach(analysisDataItem => {
        analysisDataItem.dataList.forEach((item, index) => {
          const name = this.getLegendName(
            item.showLabel,
            index,
            item.globalFilerName,
            item.groupBy,
            item.eventChineseName,
            item.eventName,
          );

          let data = [];

          // 趋势
          if (chartShowType === 1) {
            let values = [];
            if (`${chartShowDate}` === '-1') {
              values = item.summary;
            }
            if (`${chartShowDate}` !== '-1') {
              values = item.details.find(detail => detail[0] === chartShowDate) || [];
            }

            data = values
              .slice(2, item.summary.length)
              .map(value => (value && value.length === 2 ? numeral(value[1]).value() : 0));
          }

          // 对比
          if (chartShowType === 2) {
            const values =
              item.details.map(detail => detail.slice(2, detail.length)[parseInt(chartShowDate, 10)]) || [];
            data = values.map(value => (value && value.length === 2 ? numeral(value[1]).value() : 0));
          }

          seriesData.push({ name, type: 'line', data });
        });
      });

      return seriesData.slice(0, 10);
    },
    getRegion() {
      const { dataList } = this.analysisData[0];
      const { isCalcToday, followStartDay, followEndDay, timeUnit } = this;
      const region = [];

      if (timeUnit === 'day') {
        if (isCalcToday) {
          region.push(1);
        }
        for (let i = followStartDay; i <= followEndDay; i++) {
          region.push(i);
        }
      }
      if (timeUnit === 'week' || timeUnit === 'month') {
        const totalNum = dataList[0].summary.length - 2;
        let followStart = isCalcToday ? 1 : 2;
        for (let i = 0; i < totalNum; i++) {
          region.push(followStart);
          followStart += 1;
        }
      }
      return region;
    },
    getRegionItemText(regionItem) {
      return regionItem === 1
        ? `当${DEFAULT_TIME_UNIT_ENUM[this.timeUnit]}`
        : `第${regionItem}${DEFAULT_TIME_UNIT_ENUM[this.timeUnit]}`;
    },
    getEventNameText(eventName) {
      const { timeUnit } = this;
      if (timeUnit === 'day') {
        return dayjs(eventName).format('MM-DD');
      }
      if (timeUnit === 'week') {
        const arr = eventName.split('-');
        return `${dayjs(arr[0]).format('MM-DD')}~${dayjs(arr[1]).format('MM-DD')}`;
      }
      const arr = eventName.split('-');
      return dayjs(arr[0]).format('YYYY-MM');
    },
    getLegendName(showLabel, globalFilterIndex, globalFilerName, groupBy, eventChineseName, eventName) {
      let legendName = `${showLabel}${globalFilterIndex} `;
      if (globalFilerName) {
        legendName += `${globalFilerName}；`;
      }
      if (groupBy) {
        legendName += `${groupBy}；`;
      }
      legendName += `${eventChineseName || eventName}`;
      return legendName;
    },
    ratioFormat(num, format = '0.00%') {
      return numeral(num).format(format);
    },
  },
};
</script>

<style scoped lang="less">
.chart-cont {
  width: 100%;
  height: 100%;
}
</style>
